import React, { useRef, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Product } from '../api/products';
import ProductCard from './ProductCard';

interface BestSellersProps {
    products: Product[];
    onQuickOrder: (product: Product) => void;
}

const BestSellers: React.FC<BestSellersProps> = ({ products, onQuickOrder }) => {
    const { t, i18n } = useTranslation();
    const isRTL = i18n.dir() === 'rtl';
    const [width, setWidth] = useState(0);
    const [scrollAmount, setScrollAmount] = useState(0);

    const carouselRef = useRef<HTMLDivElement>(null);
    const innerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (innerRef.current && carouselRef.current) {
            setWidth(innerRef.current.scrollWidth - carouselRef.current.offsetWidth);
        }
    }, [products]);

    // Take more products for a better slideshow experience
    const bestSellers = products.slice(0, 10);

    const scroll = (direction: 'left' | 'right') => {
        if (carouselRef.current) {
            const scrollDistance = 352; // Card width (320px) + gap (32px)

            if (isRTL) {
                // RTL Logic:
                // 'left' button (Next) -> Increases X (Move Right to reveal Left items)
                // 'right' button (Prev) -> Decreases X
                // Limits: 0 to width (Positive)
                const newScroll = direction === 'left'
                    ? Math.min(width, scrollAmount + scrollDistance)
                    : Math.max(0, scrollAmount - scrollDistance);
                setScrollAmount(newScroll);
            } else {
                // LTR Logic:
                // 'left' button (Prev) -> Increases X (Back out left items) -> limit 0
                // 'right' button (Next) -> Decreases X (Enter right items) -> limit -width
                const newScroll = direction === 'left'
                    ? Math.min(0, scrollAmount + scrollDistance)
                    : Math.max(-width, scrollAmount - scrollDistance);
                setScrollAmount(newScroll);
            }
        }
    };

    if (bestSellers.length === 0) return null;

    return (
        <section className="py-20 bg-neutral-50 overflow-hidden">
            <div className="container mx-auto px-4">
                <div className="flex flex-col md:flex-row justify-between items-end mb-12">
                    <div className="w-full md:w-auto text-start">
                        <motion.span
                            className="text-primary font-semibold tracking-wider uppercase text-sm"
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                        >
                            {t('home.topRated', 'Top Rated')}
                        </motion.span>
                        <motion.h2
                            className="text-3xl md:text-4xl font-bold text-dark mt-2"
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: 0.1 }}
                        >
                            {t('home.bestSellers', 'Best Sellers')}
                        </motion.h2>
                    </div>

                    <div className={`flex gap-4 mt-6 md:mt-0 ${isRTL ? 'flex-row-reverse' : ''}`}>
                        <button
                            onClick={() => scroll('left')}
                            disabled={isRTL ? scrollAmount >= width : scrollAmount === 0}
                            className={`w-12 h-12 rounded-full flex items-center justify-center transition-all duration-300 ${(isRTL ? scrollAmount >= width : scrollAmount === 0)
                                ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                : 'bg-white text-dark shadow-md hover:bg-primary hover:text-white'
                                }`}
                            aria-label={isRTL ? "Next" : "Previous"}
                        >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                            </svg>
                        </button>
                        <button
                            onClick={() => scroll('right')}
                            disabled={isRTL ? scrollAmount <= 0 : scrollAmount <= -width}
                            className={`w-12 h-12 rounded-full flex items-center justify-center transition-all duration-300 ${(isRTL ? scrollAmount <= 0 : scrollAmount <= -width)
                                ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                : 'bg-white text-dark shadow-md hover:bg-primary hover:text-white'
                                }`}
                            aria-label={isRTL ? "Previous" : "Next"}
                        >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                        </button>
                    </div>
                </div>

                <div
                    ref={carouselRef}
                    className="overflow-hidden"
                >
                    <motion.div
                        ref={innerRef}
                        drag="x"
                        dragConstraints={{
                            right: isRTL ? width : 0,
                            left: isRTL ? 0 : -width
                        }}
                        animate={{ x: scrollAmount }}
                        transition={{ type: "spring", stiffness: 300, damping: 30 }}
                        onDragEnd={(_, info) => {
                            // Update scrollAmount when dragging ends to keep buttons in sync
                            setScrollAmount(prev => {
                                const newAmount = prev + info.offset.x;
                                if (isRTL) {
                                    return Math.max(0, Math.min(width, newAmount));
                                }
                                return Math.max(-width, Math.min(0, newAmount));
                            });
                        }}
                        className="flex gap-8 pb-8 relative"
                    >
                        {bestSellers.map((product, index) => (
                            <motion.div
                                key={product.id}
                                className="min-w-[280px] md:min-w-[320px]"
                            >
                                <ProductCard
                                    product={product}
                                    onQuickOrder={onQuickOrder}
                                />
                            </motion.div>
                        ))}
                    </motion.div>
                </div>
            </div>
        </section>
    );
};

export default BestSellers;
